/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from 'react';
import {
  Button,
  Flex,
  Form,
  GetProp,
  Input,
  List,
  Typography,
  Card,
  Space,
  Progress,
} from 'antd';
import {
  ArrowRightLeft,
  Shield,
  ChevronLeft,
  CheckCircle,
} from 'lucide-react';
import { useLanguage } from 'hooks/Language/useLanguage';
import GenerateBill from '../GenerateBill';

import './index.scss';

// Improved type definitions
interface ITransferConfirmation {
  label: string;
  value: string;
}

interface TransferConfirmationProps {
  items: ITransferConfirmation[];
  title?: string;
  onHandleConfirm?: () => void;
  onHandleBack?: () => void;
  isLoading?: boolean;
  onValuesChange?: GetProp<typeof Input.OTP, 'onChange'>;
  openPrintTransaction?: boolean;
  sendFundsToGatewayData?: any;
  onHandleClosePrintPage?: () => void;
  printReceipt?: boolean;
  onHandlePrintReceipt?: () => void;
  hideButtons?: boolean;
}

const { Title, Text } = Typography;

const TransferConfirmation: React.FC<TransferConfirmationProps> = ({
  items,
  onHandleConfirm,
  onHandleBack,
  isLoading,
  title = 'Transfer Confirmation',
  onValuesChange,
  openPrintTransaction,
  sendFundsToGatewayData,
  onHandleClosePrintPage,
  printReceipt,
  hideButtons,
}) => {
  const { translate } = useLanguage();
  const [pinComplete, setPinComplete] = useState(false);

  const handlePinChange = (value: string) => {
    if (onValuesChange) {
      onValuesChange(value);
    }

    setPinComplete(value.length === 6);
  };

  return (
    <Card
      size="small"
      bordered={false}
      style={{ padding: '0px' }}
      className="transaction-confirmation-card"
    >
      <Flex vertical gap="middle">
        <Flex
          justify="space-between"
          align="center"
          className="header-container"
        >
          <Space>
            <div className="title-badge" />
            <Title level={4} className="no-margin">
              {translate(title)}
            </Title>
          </Space>
          <div className="icon-container">
            <ArrowRightLeft
              strokeWidth={1.5}
              size={24}
              className="transfer-icon"
            />
          </div>
        </Flex>

        <div className="confirmation-content">
          <Card size="small" className="details-card">
            <List
              className="transfer-details-list"
              dataSource={items}
              renderItem={(item) => (
                <List.Item>
                  <Flex
                    justify="space-between"
                    style={{ width: '100%' }}
                  >
                    <Text type="secondary">{item.label}</Text>
                    <Text strong className="detail-value">
                      {item.value}
                    </Text>
                  </Flex>
                </List.Item>
              )}
            />
          </Card>

          <Form layout="vertical" className="confirmation-form">
            <div className="secure-pin-container">
              <div className="pin-header">
                <Space align="center">
                  <Shield size={20} className="security-icon" />
                  <Text strong>
                    {translate('Enter PIN to confirm')}
                  </Text>
                </Space>
              </div>

              <Form.Item className="pin-input-container">
                <Input.OTP
                  length={6}
                  className="pin-input"
                  onChange={handlePinChange}
                  type="password"
                  mask="●"
                  // inputType="numeric"
                  autoFocus
                  size="large"
                />
                {pinComplete && (
                  <CheckCircle
                    size={20}
                    className="pin-complete-icon"
                  />
                )}
              </Form.Item>

              <Progress
                percent={pinComplete ? 100 : 0}
                showInfo={false}
                className="pin-progress"
              />
            </div>

            {!hideButtons && (
              <Form.Item className="action-buttons-container">
                <Flex
                  justify="space-between"
                  align="center"
                  className="button-group"
                >
                  <Button
                    variant="filled"
                    color="volcano"
                    onClick={onHandleBack}
                    size="large"
                    className="back-button"
                    icon={<ChevronLeft size={16} />}
                  >
                    {translate('Back')}
                  </Button>
                  <Button
                    type="primary"
                    htmlType="submit"
                    onClick={onHandleConfirm}
                    loading={isLoading}
                    size="large"
                    className="confirm-button"
                    disabled={!pinComplete}
                  >
                    {translate('Confirm')}
                  </Button>
                </Flex>
              </Form.Item>
            )}
          </Form>
        </div>
      </Flex>

      <GenerateBill
        open={openPrintTransaction && printReceipt}
        transactionData={sendFundsToGatewayData}
        onHandleClosePrintPage={onHandleClosePrintPage}
      />
    </Card>
  );
};

export default TransferConfirmation;
